import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { EventMessage, EventType, AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { ImageCroppedEvent, LoadedImage, ImageTransform } from 'ngx-image-cropper';
import { debounceTime, filter, firstValueFrom } from "rxjs";
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

import { HriuApiService, EmployeeSearchRequest, EmployeeLookup, UploadPhotoRequest, CustomResponse, ImageType } from 'pcl-hrimageuploader-fa';

import { HriuConsole } from 'src/app/utilities/console-utils';
import { PhotoUploadingDialog } from 'src/app/components/dialogs/photo-uploading/photo-uploading-dialog';
import { PhotoUploadSuccessDialog } from 'src/app/components/dialogs/photo-upload-success/photo-upload-success-dialog';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { UtilitiesService } from 'src/app/services/utilities.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss']
})

export class HomeComponent implements OnInit {
    public form: FormGroup;
    
    // employee lookup properties
    public filteredEmployees: EmployeeLookup[] = [];
    public selectedEmployee: EmployeeLookup | null = null;
    public selectedEmployeeDistrictName: string = '';
    public isLoadingEmployees: boolean = false;
    private currentFilter: EmployeeSearchRequest = {
        firstName: '',
        lastName: '',
        email: '',
        maxResults: 50 
    };
    private suppressFilterUpdates: boolean = false;

    // boolean display flags
    // TODO: this wraps the entire component html - should probably just not allow component to load if user is not logged in instead
    public loginDisplay: boolean = false;
    public isImageLoaded: boolean = false;
    public showAlignmentGuide: boolean = true;
    public isDarkOverlay: boolean = true; // New property for overlay contrast

    // ngx-image-cropper properties
    public imageChangedEvent: Event | null = null;
    public loadedImage: LoadedImage | null = null;
    public croppedImageBase64: string | null | undefined = '';
    public resizedImage200Base64: string | null | undefined = '';
    public resizedImageECBase64: string | null | undefined = '';
    public transform: ImageTransform = {};
    public rotation = 0; 
    public scale = 1;
    public originalImageResolution: number | null = null;
    public croppedImageHeight: number = 0;

    // getters
    get formValue() { return this.form.value; }

    constructor(
            private authService: MsalService, 
            private msalBroadcastService: MsalBroadcastService,
            public dialog: Dialog,
            private snackBarService: SnackbarService,
            private hriuApiService: HriuApiService
        ) {
            this.form = new FormGroup({
                uen: new FormControl('', [Validators.required]),
                firstName: new FormControl('', [Validators.required]),
                lastName: new FormControl('', [Validators.required]),
                workEmail: new FormControl('', [Validators.required]),
                districtNumber: new FormControl('', []),
                district: new FormControl('', []),
                employeeNumber: new FormControl('', []),
                personalConsent: new FormControl('', []),
                validPhotoUploaded: new FormControl(false, [Validators.requiredTrue])
            },{
                validators: [
                    this.autocompletionValidator
                ]
            });
    }
    
    public ngOnInit(): void { 
        // TODO: generated client is now brought in, this logic for handling login success and setting active account should probably be moved to a service provider rather than component logic
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
            )
            .subscribe((result: EventMessage) => {
                const payload = result.payload as AuthenticationResult;
                
                this.authService.instance.setActiveAccount(payload.account);

                localStorage.setItem('msal.accessToken.key', payload.accessToken);
            });
        
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None)
            )
            .subscribe(() => {
                this.setLoginDisplay();
            });
        
        // One subscription to the entire form
        this.form.valueChanges
            .pipe(
                debounceTime(300),
                filter(() => !this.suppressFilterUpdates)
            ).subscribe((formValue) => {
                this.currentFilter.firstName = formValue.firstName || '';
                this.currentFilter.lastName = formValue.lastName || '';
                this.currentFilter.email = formValue.workEmail || '';
            
                this.searchEmployee();
            });
    }

    private setLoginDisplay(): void {
        this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    }

    public fileChangeEvent(event: Event): void {
        const fileInput = event.target as EventTarget & { files: FileList };
        this.imageChangedEvent = event;
        this.originalImageResolution = fileInput.files[0].size;
        const mbPhotoSizeLimit = 10 * 1000 * 1000; // 10MB in bytes
        if (this.originalImageResolution >= mbPhotoSizeLimit) {
            this.snackBarService.showErrorSnackbar("File size exceeds 10MB limit. Please choose a smaller file.");
            this.form.controls['validPhotoUploaded'].setValue(false);
            this.fileClear();
        } else {
            this.form.controls['validPhotoUploaded'].setValue(true);
        }
    }    
    
    public imageCropped(event: ImageCroppedEvent): void {
        this.croppedImageBase64 = event.base64;
        this.croppedImageHeight = event.height;
        this.updateResizedImages();
    }

    public imageLoaded(image: LoadedImage): void {
        this.loadedImage = image;
        this.isImageLoaded = true;

        // Analyze image brightness to adjust overlay contrast
        if (image?.original?.base64) {
            this.analyzeImageBrightness(image.original.base64)
                .then((isBright: boolean) => {
                    this.isDarkOverlay = isBright;
                });
        }
    }

    public loadImageFailed(): void {
        this.snackBarService.showErrorSnackbar("Failed to load photo, please try another one");
    }

    public imageRotate(): void {
        this.rotation -= 90;
        this.transform = {
            ...this.transform,
            rotate: this.rotation
        };
    }

    // this function 'click' the HTML element fileInput, this element is bind to the native input tag to invoke a file explorer dialog 
    public fileUpload(fileInput: HTMLElement):void {
        fileInput.click();
    }
    
    // when file input is clicked, clear the cache on that button
    // if this is skipped, users will not be able to select the same file back to back 
    public fileUploadOnClick(e: any): void{
        e.target.value = "";
    }

    public fileClear(): void {
        // reset all image and file vars
        this.loadedImage = null;
        this.imageChangedEvent = null;
        this.croppedImageBase64 = '';
        this.isImageLoaded = false;
    }

    private updateResizedImages(): void {
        // Reset resized images
        this.resizedImage200Base64 = '';
        this.resizedImageECBase64 = '';

        // Resize the cropped image to 200x200 and 180x240
        this.resizeImage(200, 200)
            .then((resizedBase64) => {
                this.resizedImage200Base64 = resizedBase64;
            })
            .catch((error) => {
                HriuConsole.error("Error resizing image to 200x200", error);
                this.snackBarService.showErrorSnackbar("Invalid Photo");
            });

        this.resizeImage(180, 240)
            .then((resizedBase64) => {
                this.resizedImageECBase64 = resizedBase64;
            })
            .catch((error) => {
                HriuConsole.error("Error resizing image to 180x240", error);
                this.snackBarService.showErrorSnackbar("Invalid Photo");
            });
    }

    /**
     * Resizes and crops the current cropped image to the specified width and height,
     * maintaining the aspect ratio by adding padding with the background colour as needed.
     *
     * @param width - The target width for the resized image in pixels.
     * @param height - The target height for the resized image in pixels.
     * @returns A Promise that resolves to a base64-encoded JPEG string of the resized image.
     *
     * @throws Will reject the promise if the canvas context cannot be obtained,
     *         if the cropped image is invalid, or if the image fails to load.
     *
     * @example
     * ```typescript
     * this.resizeImage(200, 200).then(resizedBase64 => {
     *   // Use the resized image
     * }).catch(error => {
     *   // Handle error
     * });
     * ```
     */
    private resizeImage(width: number, height: number): Promise<string> {
        return new Promise((resolve, reject) => {
            const pclGreen: string = "#373a37";
            // Create canvas for target dimensions
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
                reject("Unable to get canvas context");
                return;
            }
            
            if (!this.croppedImageBase64) {
                reject("Invalid Image");
                return;
            }

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high'; // Use high quality for better results
            
            // Fill the canvas with the background color first
            ctx.fillStyle = pclGreen;
            ctx.fillRect(0, 0, width, height);
            
            const img = new Image();
            img.src = this.croppedImageBase64;
            
            img.onload = () => {
                // Get source dimensions from the cropped image
                const sourceWidth = this.croppedImageHeight;
                const sourceHeight = this.croppedImageHeight;
                
                // Calculate aspect ratios
                const sourceRatio = sourceWidth / sourceHeight;
                const targetRatio = width / height;
                
                let drawWidth: number;
                let drawHeight: number;
                let drawX: number;
                let drawY: number;
                
                // Calculate dimensions to fit image within target while maintaining aspect ratio
                if (targetRatio > sourceRatio) {
                    // Target is wider than source - fit to height, center horizontally
                    drawHeight = height;
                    drawWidth = height * sourceRatio;
                    drawX = (width - drawWidth) / 2; // Center horizontally
                    drawY = 0;
                } else {
                    // Target is taller than source - fit to width, center vertically
                    drawWidth = width;
                    drawHeight = width / sourceRatio;
                    drawX = 0;
                    drawY = (height - drawHeight) / 2; // Center vertically
                }
                
                // Draw the image centered with background color padding
                ctx.drawImage(
                    img,
                    0, 0, sourceWidth, sourceHeight, // Use entire source image
                    drawX, drawY, drawWidth, drawHeight // Destination rectangle (centered)
                );
                
                // Apply subtle sharpening filter to counteract softness from resizing
                const imageData = ctx.getImageData(0, 0, width, height);
                const data = imageData.data;
                const factor = 0.2; // Subtle sharpening strength
                
                for (let i = width * 4; i < data.length - width * 4; i += 4) {
                    if ((i / 4) % width !== 0 && (i / 4) % width !== width - 1) {
                        for (let channel = 0; channel < 3; channel++) { // RGB channels only
                            const current = data[i + channel];
                            const above = data[i - width * 4 + channel];
                            const below = data[i + width * 4 + channel];
                            const left = data[i - 4 + channel];
                            const right = data[i + 4 + channel];
                            
                            const sharpened = current * (1 + 4 * factor) - factor * (above + below + left + right);
                            data[i + channel] = Math.max(0, Math.min(255, sharpened));
                        }
                    }
                }
                ctx.putImageData(imageData, 0, 0);

                const resizedImageBase64 = canvas.toDataURL('image/jpeg', 1);
                resolve(resizedImageBase64);
            };
            
            img.onerror = () => {
                reject("Failed to load image");
            };
        });    
    }

    /**
     * Uploads the cropped and resized employee photo to Central Store and more through the HRIU Function App API.
     * Validates the form, resizes the image to multiple resolutions (180x240, 200x200), and uploads
     * all image variants including the original and cropped versions.
     * 
     * @returns A Promise that resolves when the upload is complete and success dialog is shown
     * 
     * @throws Will display error snackbar and reject if form validation fails,
     *         image resizing fails, SharePoint service is not initialized, or upload request fails
     */
    public async uploadImage(): Promise<void> {
        // Suppress filter updates during form validation to prevent unnecessary API calls
        this.suppressFilterUpdates = true;
        this.form.updateValueAndValidity();

        if (this.form.invalid || !this.loadedImage)
        {
            // Re-enable filter updates if validation fails
            this.suppressFilterUpdates = false;
            this.snackBarService.showErrorSnackbar("Please pick both an employee from search and a photo to upload.");
            return;
        }

        this.dialog.open(PhotoUploadingDialog, { disableClose: true });

        // upload the photo with the original resolution
        let uploadPhotoRequest: UploadPhotoRequest = 
        {
            image: [{
                imageString: this.resizedImageECBase64 ?? '',
                resolution: 180,
                imageType: ImageType._180
            },
            {
                imageString: this.resizedImage200Base64 ?? '',
                resolution: 200,
                imageType: ImageType._200
            },
            {
                imageString: this.loadedImage?.original.base64 ?? '',
                resolution: this.originalImageResolution ?? 0,
                imageType: ImageType.Base
            },
            {
                imageString: this.croppedImageBase64 ?? '',
                resolution: this.croppedImageHeight,
                imageType: ImageType.Cropped
            }],
            description: `${new Date().toLocaleDateString('default', {month: 'short'})} ${new Date().getFullYear()}`,
            firstName: this.selectedEmployee?.firstName || "",
            lastName: this.selectedEmployee?.lastName || "",
            uen: this.selectedEmployee?.uen || "",
            uploaderOfficeId: '',
            uploaderOffice: ''
        };
        
        try {
            await firstValueFrom(this.hriuApiService.submitPhotoUpdate(uploadPhotoRequest));

            this.suppressFilterUpdates = true;
            this.fileClear();
            this.form.reset();
            this.clearEmployeeSelected();
            this.dialog.closeAll();

            const successDialogRef = this.dialog.open(PhotoUploadSuccessDialog);
            setTimeout(() => { successDialogRef?.close(); }, 6000);
            setTimeout(() => { this.suppressFilterUpdates = false; }, 100);
        } catch (err) {
            const errorRes = err as HttpErrorResponse;
            const serverError = errorRes.error as CustomResponse;

            HriuConsole.error(errorRes);
            this.dialog.closeAll();
            this.suppressFilterUpdates = false;

            if (serverError?.code == HttpStatusCode.NotFound.toString()) {
                this.snackBarService.showErrorSnackbar(`Employee ${uploadPhotoRequest.firstName} ${uploadPhotoRequest.lastName} not found in SuccessFactors.`);
                return;
            }

            const succeeded: string[] = (serverError?.systemsSucceeded as string[] | undefined) ?? [];
            const failed: string[] = (serverError?.systemsFailed as string[] | undefined) ?? [];

            if (succeeded.length > 0 && failed.length > 0) {
                const succeededNames = succeeded.map(op => this.getIntegrationDisplayName(op));
                const failedNames = failed.map(op => this.getIntegrationDisplayName(op));
                const msg = `Partial Failure: ${this.formatIntegrationList(succeededNames)} update${succeededNames.length > 1 ? 's' : ''} complete. ${this.formatIntegrationList(failedNames)} upload${failedNames.length > 1 ? 's' : ''} failed. Please contact your system administrator.`;
                this.snackBarService.showErrorSnackbar(msg, "Close", { duration: 10000000000000000000 });
                return;
            }

            this.snackBarService.showErrorSnackbar("Oops! Something Went Wrong. Please Contact Your System Administrator.", "Close", { duration: 10000000000000000000 }); // dumb hack to make this error stay up until a user closes it
        }
    }

    /**
     * Searches for employees using the HRIU API based on current filter criteria.
     * Only executes the search if at least one search field (firstName, lastName, or email) has a value.
     * Updates the filteredEmployees array with search results for autocomplete functionality.
     * 
     * @throws Will display error snackbar if the API request fails or returns an error
     * 
     * @example
     * ```typescript
     * // Automatically called when form values change after debouncing
     * this.searchEmployee();
     * ```
     */
    public searchEmployee(): void {
        //  all fields are empty? skip calling api and present no options
        if (this.currentFilter.firstName === '' && this.currentFilter.lastName === '' && this.currentFilter.email === '') {
            return
        };
        
        this.filteredEmployees = [];
        this.isLoadingEmployees = true;

        this.hriuApiService.searchODataForEmployee(this.currentFilter).subscribe({
            next: (response) => {
                this.filteredEmployees = [...response];
                this.isLoadingEmployees = false;
            },
            error: (error) => {
                HriuConsole.error("Error searching for employee", error);   
                this.snackBarService.showErrorSnackbar("Error Searching Employees. Please Contact your Administrator.");
                this.isLoadingEmployees = false;
            }
        });    
    }

    /**
     * Handles employee selection from the autocomplete dropdown.
     * Populates all form fields with the selected employee's data and converts district number
     * to district name. Clears any existing image data and temporarily suppresses filter updates
     * to prevent infinite loops during form patching.
     * 
     * @param e - The Material autocomplete selection event containing the selected employee data
     * 
     * @example
     * ```typescript
     * // Called automatically when user selects an employee from autocomplete
     * // Template: (optionSelected)="employeeSelected($event)"
     * ```
     */
    public employeeSelected(e: MatAutocompleteSelectedEvent): void {
        if (!e.option.value) {
            this.clearEmployeeSelected();
            return;
        }

        this.suppressFilterUpdates = true;

        this.form.controls['uen'].patchValue(e.option.value.uen);
        this.form.controls['firstName'].patchValue(e.option.value.firstName);
        this.form.controls['lastName'].patchValue(e.option.value.lastName);
        this.form.controls['workEmail'].patchValue(e.option.value.workEmail);
        this.form.controls['districtNumber'].patchValue(e.option.value.districtNumber);
        this.selectedEmployeeDistrictName = UtilitiesService.convertDistrictNumberToDistrictName(e.option.value.districtNumber);
        this.selectedEmployee = { ...e.option.value } ;
        
        // Use a longer timeout to ensure all form changes have propagated
        setTimeout(() => {
            this.suppressFilterUpdates = false;
        }, 500);
    }

    public clearEmployeeSelected(): void {
        this.suppressFilterUpdates = true;
        this.form.controls['uen'].patchValue("");
        this.form.controls['firstName'].patchValue("");
        this.form.controls['lastName'].patchValue("");
        this.form.controls['workEmail'].patchValue("");
        this.form.controls['districtNumber'].patchValue("");
        this.form.controls['district'].patchValue(""); 
        this.filteredEmployees = [];
        this.isLoadingEmployees = false;
        this.currentFilter.firstName = '';
        this.currentFilter.lastName = '';  
        this.currentFilter.email = '';
        this.selectedEmployee = null;
        this.selectedEmployeeDistrictName = '';

        // wrap in setTimeout to place on the stack/defer resetting until after value patches complete
        setTimeout(() => this.suppressFilterUpdates = false);
    }

    /**
     * Custom form validator that ensures an employee is selected through autocomplete functionality.
     * Validates that if any employee-related form fields have values, the employee must be properly
     * selected from the autocomplete dropdown (selectedEmployee is not null).
     * 
     * @param control - The AbstractControl being validated (typically the FormGroup)
     * @returns ValidationErrors object with autocompleted: false if validation fails, null if valid
     * 
     * @example
     * ```typescript
     * // Used in FormGroup constructor
     * this.form = new FormGroup({...}, {
     *   validators: [this.autocompletionValidator]
     * });
     * ```
     */
    public autocompletionValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        if (!this.form) return null;

        if ((this.form.controls['uen'].value || this.form.controls['firstName'].value || this.form.controls['lastName'].value || this.form.controls['workEmail'].value) && this.selectedEmployee === null)
        {
			return { autocompleted: false };
		}

		return null;
	};

    private getIntegrationDisplayName(operation: string): string {
        const displayNames: Record<string, string> = {
            'SharePoint': 'Sharepoint',
            'DataHub': 'DataHub',
            'SuccessFactors': 'Employee Central',
            'MicrosoftOffice': 'Microsoft Teams'
        };
        return displayNames[operation] ?? operation;
    }

    private formatIntegrationList(items: string[]): string {
        if (items.length === 1) return items[0];
        if (items.length === 2) return `${items[0]} and ${items[1]}`;
        return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
    }

    /**
     * Analyzes the brightness of the uploaded image to determine if a dark or light overlay should be used.
     * Samples pixels from the center region where the person overlay will appear.
     * 
     * @param imageBase64 - The base64 string of the image to analyze
     * @returns Promise<boolean> - true if overlay should be dark, false if it should be light
     */
    private analyzeImageBrightness(imageBase64: string): Promise<boolean> {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                if (!ctx) {
                    resolve(false); // Default to light overlay
                    return;
                }
                
                // Set canvas size to match image
                canvas.width = img.width;
                canvas.height = img.height;
                
                // Draw image to canvas
                ctx.drawImage(img, 0, 0);
                
                // Sample pixels from center region (where person overlay appears)
                const centerX = Math.floor(img.width / 2);
                const centerY = Math.floor(img.height / 2);
                const sampleSize = Math.min(img.width, img.height) * 0.3; // 30% of image size
                
                let totalBrightness = 0;
                let pixelCount = 0;
                
                // Sample pixels in a grid pattern for performance
                const step = Math.max(1, Math.floor(sampleSize / 20)); // Sample ~400 pixels max
                
                for (let x = centerX - sampleSize/2; x < centerX + sampleSize/2; x += step) {
                    for (let y = centerY - sampleSize/2; y < centerY + sampleSize/2; y += step) {
                        if (x >= 0 && x < img.width && y >= 0 && y < img.height) {
                            const imageData = ctx.getImageData(x, y, 1, 1);
                            const [r, g, b] = imageData.data;
                            
                            // Calculate relative luminance (perceived brightness)
                            const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
                            totalBrightness += brightness;
                            pixelCount++;
                        }
                    }
                }
                
                const averageBrightness = totalBrightness / pixelCount;
                
                // If average brightness > 128 (bright image), use dark overlay
                // If average brightness <= 128 (dark image), use light overlay
                resolve(averageBrightness > 128);
            };
            
            img.onerror = () => {
                resolve(false); // Default to light overlay on error
            };
            
            img.src = imageBase64;
        });
    }
}


